import {
  Component,
  Input,
  ChangeDetectionStrategy,
  inject,
} from '@angular/core';
import { ClassService } from '../../services/class.service';
import { Observable, of, ReplaySubject, Subject } from 'rxjs';
import { AuthService } from '../../services/auth.service';
import { ReservationService } from '../../services/reservation.service';
import { ClassType } from '../../enums/class-type';
import { IClassWithCurrUserResModel } from '../../models/classWithCurrUserRes.model';
import { map } from 'rxjs/operators';
import { Router } from '@angular/router';
import { LayoutService } from '../../layout/layout.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ErrorMessageUtils } from '../../utils/ErrorMessageUtils';
import { ToastService } from '../../ui/overlay/toast.service';

import { BadgeComponent } from '../../ui/badge.component';
import { CardComponent } from '../../ui/card.component';
import { IconComponent } from '../../ui/icon.component';
import { SpinnerComponent } from '../../ui/spinner.component';
import { ButtonDirective } from '../../ui/button.directive';
import { AsyncPipe, DatePipe } from '@angular/common';
@Component({
  // `display: block` on the host: an Angular component element is inline by default,
  // and Tailwind's space-y-* sets margin-top, which does nothing on an inline box. As
  // an inline host this rendered with no vertical gap at all between siblings.
  host: { class: 'block' },
  selector: 'app-class-list',
  templateUrl: './class-list.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    BadgeComponent,
    CardComponent,
    IconComponent,
    SpinnerComponent,
    ButtonDirective,
    AsyncPipe,
    DatePipe,
  ],
})
export class ClassListComponent {
  private readonly toast = inject(ToastService);
  @Input() upcomingClasses: boolean = false;
  @Input() originIsAdminComponent: boolean = false;

  private selectedDateSubject: Subject<Date> = new ReplaySubject<Date>();
  private classesWithUserReservations: Subject<IClassWithCurrUserResModel[]> =
    new ReplaySubject<IClassWithCurrUserResModel[]>();

  isAdmin$: Observable<boolean> = of(false);
  isAdmin: boolean = false;
  classes$?: Observable<IClassWithCurrUserResModel[]> =
    this.classesWithUserReservations.asObservable();
  selectedDate$: Observable<Date> = this.selectedDateSubject.asObservable();

  selectedDate?: Date;
  currentUserId: string = '';

  inSubmission: boolean = false;
  isDesktop: boolean = false;

  @Input() set selectedDateInput(selectedDate: Date | undefined) {
    if (selectedDate) {
      this.upcomingClasses = false;
      this.selectedDate = selectedDate;
      this.selectedDateSubject.next(selectedDate);
      this.filterClassesByDate();
    }
  }

  constructor(
    private router: Router,
    private authService: AuthService,
    private classService: ClassService,
    private reservationService: ReservationService,
    private datePipe: DatePipe,
    private layoutService: LayoutService,
  ) {
    this.isDesktop = this.layoutService.isDesktop();
    this.isAdmin$ = this.authService.user$.pipe(
      map((user) => user?.isAdmin ?? false),
    );
    this.authService.user$.pipe(takeUntilDestroyed()).subscribe((user) => {
      this.currentUserId = user?.id ?? '';
      this.isAdmin = user?.isAdmin ?? false;
    });
    this.classService
      .getClassesWithReservationsForCurrentUser()
      .pipe(takeUntilDestroyed())
      .subscribe((classes) => {
        this.classesWithUserReservations.next(classes);
      });
    if (!this.originIsAdminComponent) {
      // The chosen date lives in a ReplaySubject on the service, so it is gone on a
      // refresh or a direct hit on /reserveer/class. Without it the date filter never
      // runs and this screen listed EVERY upcoming class with no date on it, under the
      // heading "Kies een les": a member could book a class without knowing the day.
      // Step two without a step one is not a screen, so it goes back to the calendar.
      let dateArrived = false;
      this.reservationService
        .getSelectedDate()
        .pipe(takeUntilDestroyed())
        .subscribe((date) => {
          dateArrived = true;
          this.selectedDateSubject.next(date);
          this.selectedDate = date;
          this.filterClassesByDate();
        });
      // Keyed on the route, not on originIsAdminComponent: @Input values are not set
      // until after the constructor, so reading that flag here says "member" on
      // /les-aanmaken too and bounced the admin's own screen to the member calendar.
      if (!dateArrived && this.router.url.startsWith('/reserveer/class')) {
        void this.router.navigate(['/reserveer/date']);
      }
    }
  }

  isFull(spinClass: IClassWithCurrUserResModel): boolean {
    return spinClass.currentReservations >= spinClass.maxReservations;
  }

  /** Why the Reserveer button is disabled. Empty string when booking is possible. */
  reserveDisabledReason(spinClass: IClassWithCurrUserResModel): string {
    if (this.isFull(spinClass)) {
      return 'Volzet';
    }
    if (spinClass.currentReservationsForUser >= 5 && !this.isAdmin) {
      return 'Je zit aan je maximum (5)';
    }
    return '';
  }

  /** Colour of the "vrije plaatsen" count: green plenty → orange scarce → red (nearly) full. */
  /**
   * Intensity is the one thing that distinguishes two classes at the same hour, and it
   * was not on the member's card at all. Colour carries it because that is what the
   * member is choosing between, and it gives every row a colour that means something.
   */
  typeTone(type: string): 'info' | 'neutral' {
    // Green, amber and red belong to availability on this card, so intensity may not
    // borrow them: an amber "Spinning" badge beside an amber "nearly full" bar is two
    // meanings for one colour. Easy Spinning is the one that differs from the default
    // and gets the blue; everything else stays neutral.
    return type === ClassType.EasySpinning ? 'info' : 'neutral';
  }

  /** 0-100, for the availability bar. */
  fullPercent(spinClass: IClassWithCurrUserResModel): number {
    return spinClass.maxReservations
      ? Math.round(
          (spinClass.currentReservations / spinClass.maxReservations) * 100,
        )
      : 0;
  }

  /** The bar's fill tracks how hard it is to get on, matching the free-count colour. */
  availabilityFill(spinClass: IClassWithCurrUserResModel): string {
    const free = spinClass.maxReservations - spinClass.currentReservations;
    if (free <= 0) {
      return 'bg-danger';
    }
    return free / spinClass.maxReservations <= 0.2
      ? 'bg-warning'
      : 'bg-success';
  }

  availabilityClass(spinClass: IClassWithCurrUserResModel): string {
    const free = spinClass.maxReservations - spinClass.currentReservations;
    if (free <= 0) {
      return 'text-danger-fg';
    }
    if (free / spinClass.maxReservations <= 0.2) {
      return 'text-warning-fg';
    }
    return 'text-success-fg';
  }

  filterClassesByDate(): void {
    this.classes$ = this.classesWithUserReservations.pipe(
      map((classes) =>
        classes.filter(
          (c) =>
            this.convertEpochToDateString(c.dateEpoch) ===
            this.datePipe.transform(this.selectedDate, 'dd/MM/yyyy'),
        ),
      ),
    );
  }

  convertEpochToDateString(epoch: number): string {
    if (!epoch) return '';
    return <string>this.datePipe.transform(new Date(epoch), 'dd/MM/yyyy');
  }

  toggleUpcoming(): void {
    this.upcomingClasses = !this.upcomingClasses;
    if (this.upcomingClasses) {
      this.classes$ = this.classesWithUserReservations.asObservable();
    } else {
      this.filterClassesByDate();
    }
  }

  addReservation(
    spinClass: IClassWithCurrUserResModel,
    isDisabled: boolean,
  ): void {
    if (this.currentUserId.length > 0 && !isDisabled) {
      this.inSubmission = true;
      this.reservationService
        .addReservation(this.currentUserId, spinClass.id)
        .then(() => {
          // The card must move with the toast, not with the next snapshot: see
          // ClassService.overrideOwnReservations.
          this.classService.overrideOwnReservations(
            spinClass.id,
            spinClass.currentReservationsForUser + 1,
          );
          this.toast.success('Fiets gereserveerd.');
        })
        .catch((error) => {
          this.toast.error(
            ErrorMessageUtils.determineFrontendMessageBasedOnError(error),
          );
        })
        .finally(() => {
          this.inSubmission = false;
        });
    }
  }

  removeReservation(
    spinClass: IClassWithCurrUserResModel,
    isDisabled: boolean,
  ): void {
    if (this.currentUserId.length > 0 && !isDisabled) {
      this.inSubmission = true;
      this.reservationService
        .removeReservation(this.currentUserId, spinClass.id)
        .then(() => {
          const remaining = spinClass.currentReservationsForUser - 1;
          this.classService.overrideOwnReservations(spinClass.id, remaining);
          this.toast.success('Fiets geannuleerd.', {
            label: 'Ongedaan maken',
            run: () =>
              this.addReservation(
                { ...spinClass, currentReservationsForUser: remaining },
                false,
              ),
          });
        })
        .catch((error) => {
          this.toast.error(
            ErrorMessageUtils.determineFrontendMessageBasedOnError(error),
          );
        })
        .finally(() => {
          this.inSubmission = false;
        });
    }
  }

  navigateStepBack() {
    this.router.navigate(['reserveer/date']);
  }

  navigateToMyReservations() {
    this.router.navigate(['mijn-reservaties']);
  }
}
